import i18n from 'i18next';
import {
  AutomationActionType,
  AutomationEventType,
  AutomationTriggerType,
  type AutomationWorkflow,
  type DeliveryChannel,
  DeliveryChannelType,
  type EventFilterDefinition,
  type EventPresetDefinition,
  type FilterRule,
  type FilterRules,
  type FilterRuleValue,
  FilterMode,
  FilterOperator,
  ScheduleType,
  SlackDeliveryTarget,
  TeamsDeliveryTarget,
} from '../../../../api/automations-workflows-client/automations-workflows-client.type';
import { humanizeSchedule } from '../shared/schedule.helpers';
import {
  ACTION_STEP_VISUALS,
  ACTION_TYPE_LABELS,
  BOOLEAN_VALUE_OPERATORS,
  DELIVERY_STEP_VISUAL,
  EVENT_TYPE_ICONS,
  EVENT_TYPE_LABELS,
  GROUP_ORDER,
  MANUAL_TRIGGER_VISUAL,
  MULTI_VALUE_OPERATORS,
  OPERATOR_LABELS,
  SCHEDULE_TRIGGER_VISUAL,
  TRIGGER_STEP_VISUAL,
  TRIGGER_TYPE_TO_GROUP_KEY,
} from './agent-automations.constants';
import {
  type ActionStepVisual,
  AutomationGroupKey,
  type AutomationCounts,
  type AutomationFilterOption,
  type AutomationFilterOptionInput,
  type AutomationGroup,
  GROUP_LABELS,
  type InsertAtCursorInput,
  type InsertAtCursorResult,
} from './agent-automations.type';

/**
 * Returns a human-readable label for an automation event type.
 *
 * @param {AutomationEventType | null | undefined} eventType - The event identifier returned by the BE
 * @returns {string} A friendly display label or "Event" when the type is missing
 */
function getEventLabel(eventType: AutomationEventType | null | undefined): string {
  if (!eventType) return 'Event';
  if (eventType === AutomationEventType.WORK_PERIOD_STARTED) {
    return `${i18n.t('common.work_period')} started`;
  }
  if (eventType === AutomationEventType.WORK_PERIOD_ENDED) {
    return `${i18n.t('common.work_period')} ended`;
  }
  return EVENT_TYPE_LABELS[eventType] ?? eventType;
}

/**
 * Returns the Material Symbol icon name to render alongside an event type.
 *
 * @param {AutomationEventType | null | undefined} eventType - The event identifier returned by the BE
 * @returns {string} A Material Symbols icon name; defaults to "bolt" when the type is missing
 */
function getEventIcon(eventType: AutomationEventType | null | undefined): string {
  if (!eventType) return 'bolt';
  return EVENT_TYPE_ICONS[eventType] ?? 'bolt';
}

/**
 * Returns a human-readable label for an automation action type.
 *
 * @param {AutomationActionType} actionType - The action identifier returned by the BE
 * @returns {string} A friendly display label
 */
function getActionLabel(actionType: AutomationActionType): string {
  return ACTION_TYPE_LABELS[actionType] ?? actionType;
}

/**
 * Describes how a workflow fires — the event label for EVENT triggers, a humanized cron for CRON, "Manual" for MANUAL. Used in the list-card subtext.
 *
 * @param {AutomationWorkflow} workflow - The workflow to describe
 * @returns {string} A short trigger description suitable for inline display
 */
function getTriggerDescription(workflow: AutomationWorkflow): string {
  if (workflow.trigger_type === AutomationTriggerType.CRON) {
    if (!workflow.cron_expression) return 'On schedule';
    const humanized = humanizeSchedule(ScheduleType.CRON, workflow.cron_expression, 'UTC');
    return humanized || 'On schedule';
  }
  if (workflow.trigger_type === AutomationTriggerType.MANUAL) {
    return 'Manual';
  }
  return getEventLabel(workflow.event_type);
}

/**
 * Returns the Material Symbol icon name to render alongside a workflow's trigger description.
 *
 * @param {AutomationWorkflow} workflow - The workflow to describe
 * @returns {string} A Material Symbols icon name
 */
function getTriggerIcon(workflow: AutomationWorkflow): string {
  if (workflow.trigger_type === AutomationTriggerType.CRON) return 'schedule';
  if (workflow.trigger_type === AutomationTriggerType.MANUAL) return 'pan_tool';
  return getEventIcon(workflow.event_type);
}

/**
 * Returns a friendly verb for a filter operator, falling back to the raw operator when unknown.
 *
 * @param {string} operator - The operator string returned by the BE filter registry
 * @returns {string} A human-readable label like "is any of"
 */
function getOperatorLabel(operator: string): string {
  return OPERATOR_LABELS[operator] ?? operator;
}

/**
 * Reports whether an operator accepts a list value (e.g. `is_any_of`).
 *
 * @param {string} operator - The operator string
 * @returns {boolean} True when the operator expects an array value
 */
function isMultiValueOperator(operator: string): boolean {
  return MULTI_VALUE_OPERATORS.has(operator);
}

/**
 * Reports whether an operator carries no value (e.g. `is_true`).
 *
 * @param {string} operator - The operator string
 * @returns {boolean} True when the operator has no associated value field
 */
function isBooleanOperator(operator: string): boolean {
  return BOOLEAN_VALUE_OPERATORS.has(operator);
}

/**
 * Converts API-backed filter choices into data that is safe for Mantine's
 * searchable Select and MultiSelect controls.
 *
 * Mantine lowercases option labels while searching. A null label from an
 * upstream project, portfolio, initiative, or workflow therefore throws at
 * render time and blanks the automation panel. Drop choices without a usable
 * string value and fall back to that value whenever the label is blank.
 *
 * @param {AutomationFilterOptionInput[]} items - Raw filter choices
 * @returns {AutomationFilterOption[]} Search-safe choices
 */
function buildAutomationFilterOptions(items: AutomationFilterOptionInput[]): AutomationFilterOption[] {
  return items.flatMap((item) => {
    if (typeof item.value !== 'string' || !item.value.trim()) return [];
    const label = typeof item.label === 'string' && item.label.trim() ? item.label : item.value;
    return [{ value: item.value, label }];
  });
}

/**
 * Counts workflows by overall, active, and paused buckets for the filter chips.
 *
 * @param {AutomationWorkflow[]} workflows - All workflows returned by the list endpoint
 * @returns {AutomationCounts} Bucket counts used by the filter chip row
 */
function computeAutomationCounts(workflows: AutomationWorkflow[]): AutomationCounts {
  let active = 0;
  let paused = 0;
  for (const workflow of workflows) {
    if (workflow.is_active) active += 1;
    else paused += 1;
  }
  return { all: workflows.length, active, paused };
}

/**
 * Groups workflows by trigger type (EVENT / CRON / MANUAL) in stable order.
 *
 * Active/paused status is conveyed by the card's status pill rather than by the
 * group buckets — primary axis is "how does this fire?", not whether it's on.
 *
 * @param {AutomationWorkflow[]} workflows - All workflows returned by the list endpoint
 * @returns {AutomationGroup[]} Groups to render in the list view (empty groups are omitted)
 */
function groupAutomations(workflows: AutomationWorkflow[]): AutomationGroup[] {
  const buckets = new Map<AutomationGroupKey, AutomationWorkflow[]>();
  for (const workflow of workflows) {
    const groupKey = TRIGGER_TYPE_TO_GROUP_KEY[workflow.trigger_type] ?? AutomationGroupKey.MANUAL;
    const existing = buckets.get(groupKey);
    if (existing) existing.push(workflow);
    else buckets.set(groupKey, [workflow]);
  }
  const groups: AutomationGroup[] = [];
  for (const key of GROUP_ORDER) {
    const bucket = buckets.get(key);
    if (bucket && bucket.length > 0) {
      groups.push({ key, label: GROUP_LABELS[key], workflows: bucket });
    }
  }
  return groups;
}

/**
 * Summarizes a delivery channel list as a short comma-separated phrase for the card row.
 *
 * @param {DeliveryChannel[]} channels - The persisted delivery channels
 * @returns {string} A summary like "Email + Slack channel"; "No delivery" when empty
 */
function summarizeDeliveryChannels(channels: DeliveryChannel[]): string {
  if (!channels || channels.length === 0) return 'No delivery';
  const parts: string[] = [];
  for (const channel of channels) {
    switch (channel.type) {
      case DeliveryChannelType.EMAIL:
        parts.push(`Email (${channel.recipients.length})`);
        break;
      case DeliveryChannelType.SLACK:
        if (channel.target === SlackDeliveryTarget.DM) parts.push('Slack DM');
        else parts.push(channel.channel_name ? `Slack #${channel.channel_name}` : 'Slack channel');
        break;
      case DeliveryChannelType.TEAMS:
        if (channel.target === TeamsDeliveryTarget.CHAT) parts.push('Teams DM');
        else parts.push(channel.channel_name ? `Teams #${channel.channel_name}` : 'Teams channel');
        break;
    }
  }
  return parts.join(' + ');
}

/**
 * Produces an empty FilterRules object using ALL-mode and no rules — used as the editor's blank slate.
 *
 * @returns {FilterRules} A fresh, empty filter rules container
 */
function emptyFilterRules(): FilterRules {
  return { mode: FilterMode.ALL, rules: [] };
}

/**
 * Coerces a possibly-missing or empty filter_rules payload into a usable editor state.
 *
 * @param {FilterRules | Record<string, never> | null | undefined} value - The persisted filter rules (may be `{}` for new workflows)
 * @returns {FilterRules} A populated rules container suitable for editing
 */
function normalizeFilterRules(value: FilterRules | Record<string, never> | null | undefined): FilterRules {
  if (!value || !('mode' in value) || !('rules' in value)) return emptyFilterRules();
  return {
    mode: value.mode ?? FilterMode.ALL,
    rules: Array.isArray(value.rules) ? value.rules : [],
  };
}

/**
 * Reports whether a rule carries a usable value. Boolean-operator rules
 * (`is_true`/`is_false`) intentionally carry no value and are always usable;
 * every other operator needs a non-empty string, non-empty array, or a
 * number/boolean. An empty string or empty array means "no constraint".
 *
 * @param {FilterRule} rule - The rule to inspect
 * @returns {boolean} True when the rule should be sent to the BE
 */
function isMeaningfulRule(rule: FilterRule): boolean {
  if (isBooleanOperator(rule.operator)) return true;
  const value = rule.value;
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

/**
 * Serializes an editor-side FilterRules object for the wire. Drops rules with an
 * empty value (e.g. an optional filter row the user added but left blank, or a
 * preset-seeded field they never filled) so they are never persisted as a
 * meaningless `field is ''` constraint, and returns `{}` when nothing remains so
 * the BE treats it as "fire on every event".
 *
 * @param {FilterRules} rules - The editor state
 * @returns {FilterRules | Record<string, never>} Pruned rules, or `{}` when none are meaningful
 */
function serializeFilterRules(rules: FilterRules): FilterRules | Record<string, never> {
  const meaningful = (rules.rules ?? []).filter(isMeaningfulRule);
  if (meaningful.length === 0) return {};
  return { mode: rules.mode, rules: meaningful };
}

/**
 * Returns a blank rule, defaulting its operator to the first one the field accepts.
 *
 * @param {string} fieldName - The filter field path (e.g. "target_id")
 * @param {string[]} operators - The operators the field accepts
 * @returns {FilterRule} A fresh rule for the field
 */
function blankRuleForField(fieldName: string, operators: string[]): FilterRule {
  const operator = operators[0] ?? FilterOperator.IS;
  if (isBooleanOperator(operator)) return { field: fieldName, operator };
  if (isMultiValueOperator(operator)) return { field: fieldName, operator, value: [] };
  return { field: fieldName, operator, value: '' };
}

/**
 * Seeds an editor filter_rules state with a blank rule for every required filter
 * of the given event, so the builder immediately surfaces the must-fill rows
 * (e.g. `workflow_id` for skipped_status, `project_id` for the task triggers).
 *
 * @param {AutomationEventType | null} eventType - The event whose required filters to seed
 * @param {EventFilterDefinition[]} definitions - The loaded filter definitions from the BE
 * @returns {FilterRules} ALL-mode rules with one blank rule per required filter, or empty when none
 */
function seedRequiredFilterRules(
  eventType: AutomationEventType | null,
  definitions: EventFilterDefinition[],
): FilterRules {
  if (!eventType) return emptyFilterRules();
  const definition = definitions.find((entry) => entry.event_type === eventType);
  if (!definition || definition.required_filters.length === 0) return emptyFilterRules();
  const rules: FilterRule[] = [];
  for (const fieldName of definition.required_filters) {
    const fieldDefinition = definition.optional_filters.find((entry) => entry.field === fieldName);
    if (fieldDefinition) rules.push(blankRuleForField(fieldDefinition.field, fieldDefinition.operators));
  }
  return rules.length > 0 ? { mode: FilterMode.ALL, rules } : emptyFilterRules();
}

/**
 * Builds the editor filter_rules state for a BE-served preset: an empty
 * required `project_id` rule (the user still picks the project — preset rules
 * never contain it by contract) followed by the preset's rules. Rules are
 * deep-copied so later editor mutations can't corrupt the React Query cache
 * entry the preset came from.
 *
 * @param {EventPresetDefinition} preset - The preset recipe from the filter-definitions response
 * @returns {FilterRules} ALL-mode rules: blank project_id first, then the preset's rules
 */
function buildPresetFilterRules(preset: EventPresetDefinition): FilterRules {
  return {
    mode: FilterMode.ALL,
    rules: [
      { field: 'project_id', operator: FilterOperator.IS, value: '' },
      ...preset.rules.map((rule) => ({
        ...rule,
        // Cloning preserves the runtime shape (string[] or [number, number]);
        // the assertion only undoes TS widening the copy to (string | number)[].
        value: Array.isArray(rule.value) ? ([...rule.value] as FilterRuleValue) : rule.value,
      })),
    ],
  };
}

/**
 * Returns the display labels of the event's required filters that are not yet
 * satisfied by a non-empty rule value. An empty string / empty array counts as
 * unsatisfied, so a seeded-but-blank required rule (e.g. project_id) keeps save
 * blocked. Used by the builder to gate save and drive the "add required filter"
 * banner.
 *
 * @param {EventFilterDefinition | null} eventDefinition - The selected event's definition (null when none chosen)
 * @param {FilterRule[]} rules - The current editor rules
 * @returns {string[]} Labels of unsatisfied required filters (empty when all satisfied or no event)
 */
function getUnsatisfiedRequiredFilters(eventDefinition: EventFilterDefinition | null, rules: FilterRule[]): string[] {
  if (!eventDefinition) return [];
  const labelByField = new Map(eventDefinition.optional_filters.map((field) => [field.field, field.label]));
  const missing: string[] = [];
  for (const requiredField of eventDefinition.required_filters) {
    const value = rules.find((entry) => entry.field === requiredField)?.value;
    const hasValue =
      (typeof value === 'string' && value.trim().length > 0) ||
      (Array.isArray(value) && value.length > 0) ||
      typeof value === 'number' ||
      typeof value === 'boolean';
    if (!hasValue) missing.push(labelByField.get(requiredField) ?? requiredField);
  }
  return missing;
}

/**
 * Applies a rule patch and cascades clears for filters that depend on the changed rule.
 *
 * Specifically: when the SKIPPED_STATUS trigger's workflow_id rule has its value reassigned,
 * the sibling skipped_task_status_name rule's value is cleared because the previously chosen
 * status may not exist in the newly chosen workflow's mappings.
 *
 * @param {FilterRule[]} rules - The current rule list
 * @param {number} index - Index of the rule being patched
 * @param {Partial<FilterRule>} patch - Patch applied to the rule at `index`
 * @param {AutomationEventType | null} eventType - The trigger event type currently configured
 * @returns {FilterRule[]} A new rules array with the patch applied and any cascade clears
 */
function applyRulePatchWithCascade(
  rules: FilterRule[],
  index: number,
  patch: Partial<FilterRule>,
  eventType: AutomationEventType | null,
): FilterRule[] {
  const currentRule = rules[index];
  const shouldClearStatusSibling =
    eventType === AutomationEventType.SKIPPED_STATUS && currentRule?.field === 'workflow_id' && 'value' in patch;
  return rules.map((rule, i) => {
    if (i === index) return { ...rule, ...patch };
    if (shouldClearStatusSibling && rule.field === 'skipped_task_status_name') {
      // Match the value shape of the sibling rule's operator: `is_any_of`
      // expects a list, `is` expects a string.
      const clearedValue = isMultiValueOperator(rule.operator) ? [] : '';
      return { ...rule, value: clearedValue };
    }
    return rule;
  });
}

/**
 * Returns icon + color tokens for the trigger step card, keyed by the selected trigger type.
 *
 * @param {AutomationTriggerType | undefined} triggerType - The active trigger type for this automation
 * @returns {ActionStepVisual} Visual tokens for the trigger card
 */
function getTriggerStepVisual(triggerType?: AutomationTriggerType): ActionStepVisual {
  if (triggerType === AutomationTriggerType.CRON) return SCHEDULE_TRIGGER_VISUAL;
  if (triggerType === AutomationTriggerType.MANUAL) return MANUAL_TRIGGER_VISUAL;
  return TRIGGER_STEP_VISUAL;
}

/**
 * Returns icon + color tokens for an action step card, keyed by action type.
 *
 * @param {AutomationActionType} actionType - The selected action type
 * @returns {ActionStepVisual} Visual tokens for the action card
 */
function getActionStepVisual(actionType: AutomationActionType): ActionStepVisual {
  return ACTION_STEP_VISUALS[actionType] ?? ACTION_STEP_VISUALS[AutomationActionType.STATIC_MESSAGE];
}

/**
 * Returns icon + color tokens for the delivery step card.
 *
 * @returns {ActionStepVisual} Visual tokens for the delivery card
 */
function getDeliveryStepVisual(): ActionStepVisual {
  return DELIVERY_STEP_VISUAL;
}

/**
 * Wraps a trigger variable path in the `{{trigger.<path>}}` syntax used by the workflow runner.
 *
 * @param {string} path - The variable path (e.g. "target_id")
 * @returns {string} The fully formed token to insert into a prompt
 */
function formatTriggerToken(path: string): string {
  return `{{trigger.${path}}}`;
}

/**
 * Computes the text + caret position when inserting a token into a textarea at the current selection range.
 *
 * Falls back to appending the token when no element is available (e.g. before the user has focused it).
 *
 * @param {InsertAtCursorInput} input - The textarea element, its current value, and the token to insert
 * @returns {InsertAtCursorResult} The new value and where the caret should sit afterwards
 */
function insertAtCursor({ element, currentValue, token }: InsertAtCursorInput): InsertAtCursorResult {
  if (!element) {
    return { nextValue: `${currentValue}${token}`, caretIndex: currentValue.length + token.length };
  }
  const start = element.selectionStart ?? currentValue.length;
  const end = element.selectionEnd ?? currentValue.length;
  const nextValue = `${currentValue.slice(0, start)}${token}${currentValue.slice(end)}`;
  return { nextValue, caretIndex: start + token.length };
}

export {
  applyRulePatchWithCascade,
  blankRuleForField,
  buildAutomationFilterOptions,
  buildPresetFilterRules,
  computeAutomationCounts,
  emptyFilterRules,
  formatTriggerToken,
  getActionLabel,
  getActionStepVisual,
  getDeliveryStepVisual,
  getEventIcon,
  getEventLabel,
  getOperatorLabel,
  getTriggerDescription,
  getTriggerIcon,
  getTriggerStepVisual,
  getUnsatisfiedRequiredFilters,
  groupAutomations,
  insertAtCursor,
  isBooleanOperator,
  isMultiValueOperator,
  normalizeFilterRules,
  seedRequiredFilterRules,
  serializeFilterRules,
  summarizeDeliveryChannels,
};
